import { UserWorkspaceProfile } from "@models/UserWorkspaceProfile"
import { Box, Button, FormControl, Stack, TextField, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useState } from "react";
import { useChangeDisplayName } from "hooks/useChangeDisplayName";
import { UserProfileViewContext } from "context/userProfile/UserProfileViewContext";
import { truncateString } from "helpers/stringHelper";
import { CancelButton, SaveButton } from 'components/views/profile/DisplayNameSection/DisplayNameSection.styles';
import { styled } from '@mui/material/styles';
import { UserEditStyledTextTypography } from "@theme/styledComponents";

const MobileChangeDisplayNameButton = styled(Button)(({ theme }) => ({
    marginLeft: 'auto',
    flexShrink: 0,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    borderRadius: '20px',
    width: '10rem',
    height: '40px',
}));

export const MobileDisplayNameSection = (props: { user: UserWorkspaceProfile, isProfileOfLoggedUser: boolean }) => {
    const [changingDisplayName, setChangingDisplayName] = useState(false);
    const [displayName, setDisplayName] = useState(props.user.displayName);
    const { user } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { changeDisplayName, changeDisplayNamePending } = useChangeDisplayName();
    const { refreshProfile } = useContext(UserProfileViewContext);

    const handleSave = useCallback(async () => {
        const res = await changeDisplayName(displayName, props.isProfileOfLoggedUser ? undefined : user.id);
        if (res?.success) {
            setChangingDisplayName(false);
            refreshProfile();
        }
    }, [changeDisplayName, displayName, props.isProfileOfLoggedUser, user.id, refreshProfile]);

    return (
        <Stack width='100%' direction='row' alignItems='center'>
            {changingDisplayName
                ? <>
                    <Box flex={1}>
                        <FormControl variant="outlined">
                            <TextField
                                id="displayName"
                                label={t('general.newDisplayName')}
                                variant="standard"
                                value={truncateString(displayName, 22)}
                                onChange={(e) => setDisplayName(e.target.value)}
                            />
                        </FormControl>
                    </Box>
                    <Box>
                        <CancelButton variant='outlined' onClick={() => setChangingDisplayName(false)}>
                            {t('profile.cancel')}
                        </CancelButton>
                        <SaveButton
                            loading={changeDisplayNamePending}
                            variant='outlined'
                            color='primary'
                            onClick={handleSave}
                        >
                            {t('profile.save')}
                        </SaveButton>
                    </Box>
                </>
                : <>
                    <Stack>
                        <Typography variant="caption" color={theme.palette.text.secondary}>
                            {t('profile.username')}
                        </Typography>
                        <UserEditStyledTextTypography>
                            {user.displayName}
                        </UserEditStyledTextTypography>
                    </Stack>
                    <MobileChangeDisplayNameButton variant='outlined' onClick={() => setChangingDisplayName(true)}>
                        {t('profile.changeDisplayName')}
                    </MobileChangeDisplayNameButton>
                </>
            }
        </Stack>
    );
}
